(() => {
  const rootSelector = '[data-mio-wishlist-page]';
  const tokenProductTitle = '__PRODUCT_TITLE__';
  const tokenCount = '__COUNT__';

  class MioWishlistPage {
    constructor(root) {
      if (root.dataset.mioWishlistReady === 'true') return;
      root.dataset.mioWishlistReady = 'true';

      this.root = root;
      this.grid = root.querySelector('[data-mio-e2e="wishlist-grid"]');
      this.emptyState = root.querySelector('.mio-wishlist__empty');
      this.errorState = root.querySelector('[data-wishlist-error]');
      this.actionsBar = root.querySelector('.mio-wishlist__actions');
      this.metaRow = root.querySelector('[data-mio-e2e="wishlist-meta"]');
      this.countLabel = root.querySelector('[data-mio-e2e="wishlist-count"]');
      this.liveRegion = root.querySelector('[data-mio-e2e="wishlist-live-region"]');
      this.itemTemplate = root.querySelector('[data-wishlist-item-template]');
      this.modal = document.getElementById(root.dataset.modalId);
      this.clearButton = root.querySelector('[data-wishlist-clear-open]');
      this.renderVersion = 0;
      this.waitAttempts = 0;
      this.pendingFocus = null;

      this.bindEvents();
      this.waitForWishlist();
    }

    bindEvents() {
      this.clearButton?.addEventListener('click', () => {
        this.modal?.show(this.clearButton);
      });

      this.root.addEventListener('click', (event) => {
        const removeButton = event.target.closest('[data-wishlist-remove]');
        if (removeButton) this.removeItem(removeButton);

        if (event.target.closest('[data-wishlist-retry]')) {
          this.waitAttempts = 0;
          this.showLoading();
          this.waitForWishlist();
        }
      });

      this.modal?.querySelector('[data-wishlist-clear-confirm]')?.addEventListener('click', () => {
        this.pendingFocus = { type: 'empty' };
        this.modal.hide();
        window.mioWishlist.clear();
        this.announce(this.root.dataset.clearAnnouncement);
      });

      this.modal?.querySelector('[data-wishlist-clear-cancel]')?.addEventListener('click', () => {
        this.modal.hide();
      });

      window.addEventListener('wishlist:updated', () => this.render());
    }

    waitForWishlist() {
      if (window.mioWishlist) {
        this.render();
        return;
      }

      this.waitAttempts += 1;
      if (this.waitAttempts >= 30) {
        this.showError();
        return;
      }

      window.setTimeout(() => this.waitForWishlist(), 100);
    }

    productEndpoint(handle) {
      const rootUrl = this.root.dataset.rootUrl.replace(/\/$/, '');
      return `${rootUrl}/products/${encodeURIComponent(handle)}.js`;
    }

    productUrl(rawUrl) {
      try {
        const url = new URL(rawUrl, window.location.origin);
        if (url.origin !== window.location.origin) return this.root.dataset.rootUrl;
        return `${url.pathname}${url.search}`;
      } catch (error) {
        return this.root.dataset.rootUrl;
      }
    }

    imageUrl(rawUrl) {
      if (!rawUrl) return '';

      try {
        const url = new URL(rawUrl, window.location.origin);
        url.searchParams.set('width', '720');
        return url.toString();
      } catch (error) {
        return rawUrl;
      }
    }

    formatMoney(cents) {
      return window.mioFormatMoney(cents);
    }

    createItem(product) {
      const fragment = this.itemTemplate.content.cloneNode(true);
      const item = fragment.querySelector('[data-mio-e2e="wishlist-item"]');
      const links = fragment.querySelectorAll('[data-wishlist-product-link]');
      const imageSlot = fragment.querySelector('[data-wishlist-image-slot]');
      const title = fragment.querySelector('[data-wishlist-product-title]');
      const price = fragment.querySelector('[data-wishlist-product-price]');
      const soldOut = fragment.querySelector('[data-wishlist-sold-out]');
      const removeButton = fragment.querySelector('[data-mio-e2e="wishlist-remove"]');
      const removeText = fragment.querySelector('[data-wishlist-remove-text]');
      const productUrl = this.productUrl(product.url);
      const removeLabel = this.root.dataset.removeLabel.replace(tokenProductTitle, product.title);

      item.dataset.productHandle = product.handle;
      links.forEach((link) => link.setAttribute('href', productUrl));
      if (product.featured_image) {
        const image = document.createElement('img');
        image.src = this.imageUrl(product.featured_image);
        image.alt = product.title;
        image.width = 600;
        image.height = 750;
        image.loading = 'lazy';
        image.decoding = 'async';
        imageSlot.replaceWith(image);
      } else {
        imageSlot.closest('a').setAttribute('aria-label', product.title);
      }
      title.textContent = product.title;
      price.textContent = this.formatMoney(product.price);
      removeButton.dataset.wishlistRemove = product.handle;
      removeButton.dataset.productTitle = product.title;
      removeButton.setAttribute('aria-label', removeLabel);
      removeText.textContent = removeLabel;
      soldOut.hidden = product.available;

      return fragment;
    }

    updateCount(count) {
      const text = count === 1 ? this.root.dataset.countOne : this.root.dataset.countOther.replace(tokenCount, count);
      this.countLabel.textContent = text;
    }

    announce(message) {
      this.liveRegion.textContent = '';
      window.setTimeout(() => {
        this.liveRegion.textContent = message;
      }, 0);
    }

    showLoading() {
      this.emptyState.hidden = true;
      this.errorState.hidden = true;
      this.grid.hidden = false;
      this.grid.setAttribute('aria-busy', 'true');
      this.grid.replaceChildren();

      const loading = document.createElement('p');
      loading.className = 'mio-wishlist__loading';
      loading.setAttribute('role', 'status');
      loading.textContent = this.root.dataset.loadingText || 'טוען...';
      this.grid.appendChild(loading);
    }

    showEmpty() {
      this.grid.replaceChildren();
      this.grid.hidden = true;
      this.grid.setAttribute('aria-busy', 'false');
      this.emptyState.hidden = false;
      this.errorState.hidden = true;
      this.actionsBar.hidden = true;
      this.metaRow.hidden = true;
      this.updateCount(0);
      this.restoreFocus();
    }

    showError() {
      this.grid.replaceChildren();
      this.grid.hidden = true;
      this.grid.setAttribute('aria-busy', 'false');
      this.emptyState.hidden = true;
      this.errorState.hidden = false;
      this.actionsBar.hidden = true;
      this.metaRow.hidden = true;
    }

    showItems(products) {
      const items = products.map((product) => this.createItem(product));

      this.grid.replaceChildren(...items);
      this.grid.hidden = false;
      this.grid.setAttribute('aria-busy', 'false');
      this.emptyState.hidden = true;
      this.errorState.hidden = true;
      this.actionsBar.hidden = false;
      this.metaRow.hidden = false;
      this.updateCount(products.length);
      this.restoreFocus();
    }

    restoreFocus() {
      if (!this.pendingFocus) return;

      const pendingFocus = this.pendingFocus;
      this.pendingFocus = null;
      window.setTimeout(() => {
        if (pendingFocus.type === 'empty') {
          this.emptyState.querySelector('a')?.focus();
          return;
        }

        const removeButtons = Array.from(this.grid.querySelectorAll('[data-wishlist-remove]'));
        if (!removeButtons.length) {
          this.emptyState.querySelector('a')?.focus();
          return;
        }

        removeButtons[Math.min(pendingFocus.index, removeButtons.length - 1)].focus();
      }, 0);
    }

    removeItem(button) {
      const removeButtons = Array.from(this.grid.querySelectorAll('[data-wishlist-remove]'));
      const index = removeButtons.indexOf(button);
      const productTitle = button.dataset.productTitle;

      this.pendingFocus = { type: 'remove', index: Math.max(index, 0) };
      window.mioWishlist.remove(button.dataset.wishlistRemove);
      this.announce(this.root.dataset.removeAnnouncement.replace(tokenProductTitle, productTitle));
    }

    async render() {
      const version = ++this.renderVersion;
      const handles = window.mioWishlist.getAll();

      if (!handles.length) {
        this.showEmpty();
        return;
      }

      this.grid.setAttribute('aria-busy', 'true');
      const results = await Promise.all(
        handles.map(async (handle) => {
          try {
            const response = await fetch(this.productEndpoint(handle), { headers: { Accept: 'application/json' } });
            if (response.status === 404) return { state: 'missing' };
            if (!response.ok) return { state: 'error' };
            return { state: 'ready', product: await response.json() };
          } catch (error) {
            return { state: 'error' };
          }
        }),
      );

      if (version !== this.renderVersion) return;

      const products = results.filter((result) => result.state === 'ready').map((result) => result.product);
      if (products.length) {
        this.showItems(products);
      } else if (results.some((result) => result.state === 'error')) {
        this.showError();
      } else {
        this.showEmpty();
      }
    }
  }

  const initWishlistPages = (root = document) => {
    root.querySelectorAll(rootSelector).forEach((page) => new MioWishlistPage(page));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initWishlistPages());
  } else {
    initWishlistPages();
  }

  document.addEventListener('shopify:section:load', (event) => initWishlistPages(event.target));
})();
